<script setup>
// TUINITY: "Comandos" dropdown for the AI bot. Lets staff pick a command from a
// list (with a confirmation step) instead of typing it. The chosen command is
// emitted up and always sent as a PRIVATE NOTE by ReplyBox — never to the customer.
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { getCommandsForAccount } from './commandMenu';

const emit = defineEmits(['sendCommand']);

const { currentAccount } = useAccount();

// Commands shown depend on the active client (account): CEOSA, SimpleWallet, ...
const items = computed(() => getCommandsForAccount(currentAccount.value?.name));

const isOpen = ref(false);
const selectedItem = ref(null);
const inputValue = ref('');

// Final text that will be sent (command + optional instruction).
const previewCommand = computed(() => {
  if (!selectedItem.value) return '';
  const extra = inputValue.value.trim();
  return extra
    ? `${selectedItem.value.command} ${extra}`
    : selectedItem.value.command;
});

const close = () => {
  isOpen.value = false;
  selectedItem.value = null;
  inputValue.value = '';
};

const toggle = () => {
  if (isOpen.value) close();
  else isOpen.value = true;
};

const selectItem = item => {
  selectedItem.value = item;
  inputValue.value = '';
};

const back = () => {
  selectedItem.value = null;
  inputValue.value = '';
};

const confirmSend = () => {
  emit('sendCommand', previewCommand.value);
  close();
};
</script>

<template>
  <div v-on-clickaway="close" class="relative">
    <NextButton
      v-tooltip.top-end="'Comandos del bot'"
      icon="i-ph-robot"
      label="Comandos"
      slate
      faded
      sm
      @click="toggle"
    />

    <div
      v-if="isOpen"
      class="overflow-hidden absolute bottom-full left-0 z-50 mb-2 w-72 rounded-xl border shadow-lg border-n-weak bg-n-solid-1"
      @click.stop
    >
      <!-- Step 1: command list -->
      <template v-if="!selectedItem">
        <div
          class="px-3 py-2 text-xs font-medium border-b text-n-slate-11 border-n-weak"
        >
          Comandos del bot
        </div>
        <ul class="overflow-y-auto py-1 max-h-72">
          <li v-for="item in items" :key="item.id">
            <button
              type="button"
              class="flex flex-col gap-0.5 px-3 py-2 w-full text-left hover:bg-n-alpha-1"
              @click="selectItem(item)"
            >
              <span class="text-sm text-n-slate-12">{{ item.label }}</span>
              <span class="text-xs text-n-slate-10">{{ item.description }}</span>
            </button>
          </li>
        </ul>
      </template>

      <!-- Step 2: confirm (with optional instruction box) -->
      <template v-else>
        <div class="flex flex-col gap-3 p-3">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ selectedItem.label }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ selectedItem.description }}
            </span>
          </div>

          <textarea
            v-if="selectedItem.needsInput"
            v-model="inputValue"
            rows="2"
            :placeholder="selectedItem.inputPlaceholder"
            class="px-2 py-1.5 w-full text-sm rounded-lg border resize-none border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
          />

          <div class="px-2 py-1.5 rounded-lg bg-n-alpha-1">
            <span class="text-xs text-n-slate-10">
              Se enviará como nota privada 🔒 (el cliente no lo ve)
            </span>
            <code class="block mt-0.5 text-sm break-words text-n-slate-12">
              {{ previewCommand }}
            </code>
          </div>

          <div class="flex gap-2 justify-end">
            <NextButton sm faded slate label="Cancelar" @click="back" />
            <NextButton sm color="blue" label="Enviar" @click="confirmSend" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
